import React, { useState, useEffect } from "react";
import { TextField, Select, Button, Table } from "../ui";
import { FeatherChevronDown, FeatherCheck } from "@subframe/core";
import { getAllVendors, getVendorById } from "../services/vendorApi";
import { fetchVendorPayments, VendorPayment } from "../services/api";
import { SearchableDropdown } from "../shared/presentation/components";
import { useDropdownCache } from "../shared/presentation/hooks";
import { getStoredAccountingBackend } from "../config/accounting";

interface SelectedPayment {
  payment: VendorPayment;
  amount: number;
  isSelected: boolean;
}

interface CustomerVendorPaymentRecoProps {
  transactionDate: string;
  transactionAmount: number;
  onVendorChange: (vendor: { vendor_id: string; vendor_name: string }) => void;
  onDateChange: (date: string) => void;
  onReferenceNumberChange: (refNumber: string) => void;
  onPaymentModeChange: (paymentMode: string) => void;
  onSelectedPaymentsChange: (
    payment: {
      payment_id: string;
      payment_number: string;
      amount: number;
    } | null
  ) => void;
}

const CustomerVendorPaymentReco: React.FC<CustomerVendorPaymentRecoProps> = ({
  transactionDate,
  transactionAmount,
  onVendorChange,
  onDateChange,
  onReferenceNumberChange,
  onPaymentModeChange,
  onSelectedPaymentsChange,
}) => {
  const isTally = getStoredAccountingBackend() === "tally";
  const [selectedVendor, setSelectedVendor] = useState<{
    vendor_id: string;
    vendor_name: string;
  } | null>(null);
  const [date, setDate] = useState<string>("");
  const [referenceNumber, setReferenceNumber] = useState<string>("");
  const [paymentMode, setPaymentMode] = useState<string>("Bank Transfer");
  const [vendorPayments, setVendorPayments] = useState<VendorPayment[]>([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [selectedPayments, setSelectedPayments] = useState<SelectedPayment[]>(
    []
  );

  // Dropdown cache hook
  const { getCached, setCached } = useDropdownCache();

  // Cache for selected vendors (to avoid refetching)
  const [vendorsCache, setVendorsCache] = useState<Map<string, any>>(new Map());

  // Initialize date with transaction date
  useEffect(() => {
    if (transactionDate) {
      const formattedDate = new Date(transactionDate)
        .toISOString()
        .split("T")[0];
      setDate(formattedDate);
      onDateChange(formattedDate);
    }
  }, [transactionDate, onDateChange]);

  // Initialize payment mode
  useEffect(() => {
    onPaymentModeChange(paymentMode);
  }, [paymentMode, onPaymentModeChange]);

  // Fetch vendor payments when vendor is selected (Zoho only)
  const fetchVendorPaymentsForVendor = async (vendorId: string) => {
    try {
      setLoadingPayments(true);
      const response = await fetchVendorPayments(vendorId);

      if (response.success) {
        const paymentsWithBalance = response.data.payments;
        setVendorPayments(paymentsWithBalance);

        // Initialize selected payments
        const initialSelectedPayments = paymentsWithBalance.map(
          (payment: VendorPayment) => ({
            payment,
            amount: 0,
            isSelected: false,
          })
        );
        setSelectedPayments(initialSelectedPayments);
      }
    } catch (error) {
      console.error("Failed to fetch vendor payments:", error);
      setVendorPayments([]);
      setSelectedPayments([]);
    } finally {
      setLoadingPayments(false);
    }
  };

  const handleVendorSelect = (vendor: any) => {
    const vendorId = vendor.zoho_vendorid;
    const vendorData = {
      vendor_id: vendorId,
      vendor_name: vendor.vendor_legal_name,
    };
    setSelectedVendor(vendorData);
    onVendorChange(vendorData);
    if (isTally) {
      // Tally posts a simple Receipt against the vendor ledger — no Zoho payment list.
      setVendorPayments([]);
      setSelectedPayments([]);
      onSelectedPaymentsChange({
        payment_id: "",
        payment_number: "",
        amount: transactionAmount,
      });
    } else {
      fetchVendorPaymentsForVendor(vendorId);
    }

    // Cache the selected vendor for future use
    setVendorsCache((prev) => {
      const newCache = new Map(prev);
      newCache.set(vendorId, vendor);
      return newCache;
    });

    // Cache the selected vendor in the dropdown cache
    const cacheKey = { searchTerm: "", limit: 20, offset: 0 };
    const cached = getCached<{
      items: any[];
      hasMore?: boolean;
    }>("vendors", cacheKey);

    if (cached) {
      // Check if vendor is already in cache
      const hasVendor = cached.items.some(
        (v: any) => v.zoho_vendorid === vendorId
      );
      if (!hasVendor) {
        // Prepend selected vendor to cached results
        setCached("vendors", cacheKey, {
          items: [vendor, ...cached.items],
          hasMore: cached.hasMore,
        });
      }
    }
  };

  // Fetch vendors async function for SearchableDropdown
  const fetchVendors = async (
    searchTerm: string,
    limit: number,
    offset: number,
    selectedVendorId?: string,
    selectedVendorName?: string
  ): Promise<{
    items: any[];
    hasMore?: boolean;
  }> => {
    // Only cache the initial load (offset 0, no search)
    const shouldCache = offset === 0 && !searchTerm;
    const cacheKey = { searchTerm, limit, offset };

    if (shouldCache) {
      const cached = getCached<{
        items: any[];
        hasMore?: boolean;
      }>("vendors", cacheKey);

      if (cached) {
        // Always ensure selected vendor is in the cached results (if provided)
        if (offset === 0 && selectedVendorId && selectedVendorName) {
          const hasSelected = cached.items.some(
            (vendor: any) => vendor.zoho_vendorid === selectedVendorId
          );

          if (!hasSelected) {
            // Check vendors cache first (pre-fetched vendors)
            const cachedVendor = vendorsCache.get(selectedVendorId);
            if (cachedVendor) {
              return {
                items: [cachedVendor, ...cached.items],
                hasMore: cached.hasMore,
              };
            }

            // Try to fetch selected vendor from backend
            try {
              const selectedResponse = await getVendorById(selectedVendorId);
              if (selectedResponse.data) {
                // Map the response data to vendor format
                const mappedVendor: any = {
                  vendorid: selectedResponse.data.vendorid.toString(),
                  zoho_vendorid: selectedResponse.data.zoho_vendorid,
                  vendor_legal_name: selectedResponse.data.vendor_legal_name,
                  currency_id: selectedResponse.data.currency || "",
                };
                // Store in cache for future use
                setVendorsCache((prev) => {
                  const newCache = new Map(prev);
                  newCache.set(selectedVendorId, mappedVendor);
                  return newCache;
                });
                return {
                  items: [mappedVendor, ...cached.items],
                  hasMore: cached.hasMore,
                };
              }
            } catch (error) {
              console.warn(
                "Could not fetch selected vendor from backend, using synthetic item:",
                error
              );
            }

            // Fallback: Create synthetic vendor
            const syntheticVendor: any = {
              vendorid: selectedVendorId,
              zoho_vendorid: selectedVendorId,
              vendor_legal_name: selectedVendorName,
              currency_id: "",
            };
            return {
              items: [syntheticVendor, ...cached.items],
              hasMore: cached.hasMore,
            };
          } else {
            // Selected vendor exists in cache, move it to top for better UX
            const selectedIndex = cached.items.findIndex(
              (vendor: any) => vendor.zoho_vendorid === selectedVendorId
            );
            if (selectedIndex > 0) {
              const itemsCopy = [...cached.items];
              const [selectedVendor] = itemsCopy.splice(selectedIndex, 1);
              return {
                items: [selectedVendor, ...itemsCopy],
                hasMore: cached.hasMore,
              };
            }
          }
        }
        return cached;
      }
    }

    try {
      const response = await getAllVendors({
        search: searchTerm || undefined,
        limit,
        offset,
      });

      let items = response.data || [];

      // Always ensure selected vendor is in results (if provided)
      if (offset === 0 && selectedVendorId && selectedVendorName) {
        const hasSelected = items.some(
          (vendor: any) => vendor.zoho_vendorid === selectedVendorId
        );

        if (!hasSelected) {
          // Check vendors cache first
          const cachedVendor = vendorsCache.get(selectedVendorId);
          if (cachedVendor) {
            items = [cachedVendor, ...items];
          } else {
            // Try to fetch selected vendor from backend
            try {
              const selectedResponse = await getVendorById(selectedVendorId);
              if (selectedResponse.data) {
                // Map the response data to vendor format
                const mappedVendor: any = {
                  vendorid: selectedResponse.data.vendorid.toString(),
                  zoho_vendorid: selectedResponse.data.zoho_vendorid,
                  vendor_legal_name: selectedResponse.data.vendor_legal_name,
                  currency_id: selectedResponse.data.currency || "",
                };
                // Store in cache for future use
                setVendorsCache((prev) => {
                  const newCache = new Map(prev);
                  newCache.set(selectedVendorId, mappedVendor);
                  return newCache;
                });
                items = [mappedVendor, ...items];
              } else {
                // Fallback: Create synthetic vendor
                const syntheticVendor: any = {
                  vendorid: selectedVendorId,
                  zoho_vendorid: selectedVendorId,
                  vendor_legal_name: selectedVendorName,
                  currency_id: "",
                };
                items = [syntheticVendor, ...items];
              }
            } catch (error) {
              console.warn(
                "Could not fetch selected vendor from backend, using synthetic item:",
                error
              );
              // Fallback: Create synthetic vendor
              const syntheticVendor: any = {
                vendorid: selectedVendorId,
                zoho_vendorid: selectedVendorId,
                vendor_legal_name: selectedVendorName,
                currency_id: "",
              };
              items = [syntheticVendor, ...items];
            }
          }
        } else {
          // If selected vendor exists, move it to the top (remove from current position first)
          const selectedIndex = items.findIndex(
            (vendor: any) => vendor.zoho_vendorid === selectedVendorId
          );
          if (selectedIndex >= 0) {
            // Remove from current position and prepend to top
            const [selectedVendor] = items.splice(selectedIndex, 1);
            items = [selectedVendor, ...items];
          }
        }
      }

      const result = {
        items,
        hasMore: response.pagination?.has_more || false,
      };

      // Cache the BASE results (without selected item prepended)
      if (shouldCache) {
        setCached("vendors", cacheKey, {
          items: response.data || [],
          hasMore: response.pagination?.has_more || false,
        });
      }

      return result;
    } catch (error) {
      console.error("Error fetching vendors:", error);
      return {
        items: [],
        hasMore: false,
      };
    }
  };

  const handleDateChange = (newDate: string) => {
    setDate(newDate);
    onDateChange(newDate);
  };

  const handleReferenceNumberChange = (refNum: string) => {
    setReferenceNumber(refNum);
    onReferenceNumberChange(refNum);
  };

  const handlePaymentModeChange = (mode: string) => {
    setPaymentMode(mode);
    onPaymentModeChange(mode);
  };

  const handlePaymentSelect = (paymentId: string) => {
    setSelectedPayments((prev) => {
      // Only allow one payment to be selected at a time
      const updated = prev.map((item) => {
        if (item.payment.payment_id === paymentId) {
          const newIsSelected = !item.isSelected;
          const newAmount = newIsSelected
            ? item.payment.balance > transactionAmount
              ? transactionAmount
              : item.payment.balance
            : 0;
          return {
            ...item,
            isSelected: newIsSelected,
            // amount: newIsSelected ? transactionAmount : 0,
            amount: newAmount,
          };
        }
        // Deselect all other payments
        return { ...item, isSelected: false, amount: 0 };
      });

      // Notify parent about selected payment (single payment object or null)
      const selected = updated.find(
        (item) => item.isSelected && item.amount > 0
      );

      if (selected) {
        onSelectedPaymentsChange({
          payment_id: selected.payment.payment_id,
          payment_number: selected.payment.payment_number,
          amount: selected.amount,
        });
      } else {
        onSelectedPaymentsChange(null);
      }

      return updated;
    });
  };

  return (
    <div className="flex w-full flex-col items-start gap-4">
      <div className="flex w-full flex-col items-start gap-2">
        <div className="flex w-full items-center gap-4">
          <div className="flex flex-col items-start gap-2 flex-1">
            <span className="text-body-bold font-body-bold text-default-font">
              Vendor <span className="text-error-600">*</span>
            </span>
            <SearchableDropdown
              fetchItems={(searchTerm, limit, offset) =>
                fetchVendors(
                  searchTerm,
                  limit,
                  offset,
                  selectedVendor?.vendor_id,
                  selectedVendor?.vendor_name
                )
              }
              trigger={
                <Button
                  className="w-full justify-between"
                  variant="neutral-secondary"
                  iconRight={<FeatherChevronDown />}
                >
                  <span className="truncate text-left">
                    {selectedVendor
                      ? selectedVendor.vendor_name
                      : "Select vendor"}
                  </span>
                </Button>
              }
              getSearchableText={(vendor) => vendor.vendor_legal_name}
              renderItem={(vendor) => vendor.vendor_legal_name}
              getItemKey={(vendor) => vendor.zoho_vendorid}
              onSelect={handleVendorSelect}
              selectedKey={selectedVendor?.vendor_id}
              searchPlaceholder="Search vendors..."
              emptyMessage="No vendors found"
              noResultsMessage="No vendors match your search"
              dropdownClassName="min-w-[300px]"
              initialLimit={20}
            />
          </div>
          <div className="flex flex-col items-start gap-2 flex-1">
            <span className="text-body-bold font-body-bold text-default-font">
              Date <span className="text-error-600">*</span>
            </span>
            <TextField className="w-full" label="" helpText="">
              <TextField.Input
                type="date"
                value={date}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleDateChange(event.target.value)
                }
              />
            </TextField>
          </div>
        </div>
        <div className="flex w-full items-center gap-4">
          <div className="flex flex-col items-start gap-2 flex-1">
            <span className="text-body-bold font-body-bold text-default-font">
              Reference #{" "}
              {isTally && <span className="text-error-600">*</span>}
            </span>
            <TextField className="w-full" label="" helpText="">
              <TextField.Input
                placeholder={
                  isTally
                    ? "Enter reference number (required)"
                    : "Enter reference number (optional)"
                }
                value={referenceNumber}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleReferenceNumberChange(event.target.value)
                }
              />
            </TextField>
          </div>
          <div className="flex flex-col items-start gap-2 flex-1">
            <span className="text-body-bold font-body-bold text-default-font">
              Received Via <span className="text-error-600">*</span>
            </span>
            <Select
              label=""
              helpText=""
              value={paymentMode}
              onValueChange={handlePaymentModeChange}
            >
              <Select.Item value="Bank Remittance">Bank Remittance</Select.Item>
              <Select.Item value="Bank Transfer">Bank Transfer</Select.Item>
              <Select.Item value="Cash">Cash</Select.Item>
              <Select.Item value="Cheque">Cheque</Select.Item>
              <Select.Item value="Credit Card">Credit Card</Select.Item>
              <Select.Item value="UPI">UPI</Select.Item>
            </Select>
          </div>
        </div>
      </div>

      {/* Tally: no Zoho payment picker — refund amount is the bank transaction */}
      {isTally && selectedVendor && (
        <div className="flex w-full flex-col items-start gap-2 rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-4">
          <span className="text-body-bold font-body-bold text-default-font">
            Refund Amount
          </span>
          <span className="text-body font-body text-default-font">
            ₹{transactionAmount.toLocaleString("en-IN")} will be posted as a
            Receipt against {selectedVendor.vendor_name}
          </span>
        </div>
      )}

      {/* Vendor Payments Table (Zoho only) */}
      {!isTally && selectedVendor && (
        <div className="flex w-full flex-col items-start gap-2">
          <div className="flex w-full items-center justify-between">
            <span className="text-body-bold font-body-bold text-default-font">
              Select Payment <span className="text-error-600">*</span>
            </span>
            <div className="flex items-center gap-2">
              <span className="text-caption font-caption text-neutral-400">
                Transaction Amount:
              </span>
              <span className="text-caption font-caption text-default-font">
                ₹{transactionAmount.toLocaleString("en-IN")}
              </span>
            </div>
          </div>
          {loadingPayments ? (
            <div className="flex w-full items-center justify-center rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-6">
              <span className="text-body font-body text-neutral-500">
                Loading payments...
              </span>
            </div>
          ) : vendorPayments.length > 0 ? (
            <Table
              header={
                <Table.HeaderRow>
                  <Table.HeaderCell className="w-[150px]">
                    Payment #
                  </Table.HeaderCell>
                  <Table.HeaderCell className="w-[120px]">
                    Date
                  </Table.HeaderCell>
                  <Table.HeaderCell className="w-[130px]">
                    Amount
                  </Table.HeaderCell>
                  <Table.HeaderCell className="w-[130px]">
                    Balance
                  </Table.HeaderCell>
                  <Table.HeaderCell className="w-[140px]">
                    Action
                  </Table.HeaderCell>
                </Table.HeaderRow>
              }
            >
              {selectedPayments.map((item) => (
                <Table.Row key={item.payment.payment_id}>
                  <Table.Cell className="w-[150px]">
                    <span className="text-body-bold font-body-bold text-default-font">
                      #{item.payment.payment_number}
                    </span>
                  </Table.Cell>
                  <Table.Cell className="w-[120px]">
                    <span className="text-body font-body text-default-font">
                      {item.payment.date
                        ? new Date(item.payment.date)
                            .toLocaleDateString("en-GB")
                            .split("/")
                            .join("/")
                        : "—"}
                    </span>
                  </Table.Cell>
                  <Table.Cell className="w-[130px]">
                    <span className="text-body font-body text-default-font">
                      ₹{item.payment.amount.toLocaleString("en-IN")}
                    </span>
                  </Table.Cell>
                  <Table.Cell className="w-[130px]">
                    <span className="text-body font-body text-default-font">
                      ₹{item.payment.balance.toLocaleString("en-IN")}
                    </span>
                  </Table.Cell>
                  <Table.Cell className="w-[140px]">
                    <Button
                      className={
                        item.isSelected
                          ? "bg-success-600 text-white hover:bg-success-700 w-[120px]"
                          : "w-[120px]"
                      }
                      size="small"
                      icon={item.isSelected ? <FeatherCheck /> : undefined}
                      onClick={() =>
                        handlePaymentSelect(item.payment.payment_id)
                      }
                    >
                      {item.isSelected ? "Selected" : "Select"}
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table>
          ) : (
            <div className="flex w-full items-center justify-center rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-6">
              <span className="text-body font-body text-neutral-500">
                No payments found with greater than or equal to ₹
                {transactionAmount.toLocaleString("en-IN")} for this vendor
              </span>
            </div>
          )}
        </div>
      )}

      {!isTally && !selectedVendor && (
        <div className="flex w-full flex-col items-start gap-2">
          <span className="text-body-bold font-body-bold text-default-font">
            Select Payment <span className="text-error-600">*</span>
          </span>
          <div className="flex w-full items-center justify-center rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-6">
            <span className="text-body font-body text-neutral-500">
              Select a vendor to view available payments
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerVendorPaymentReco;
